import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { HERO_SLIDES as FALLBACK } from '../mock/mock';

const Hero = ({ onPlayFilm, slides }) => {
  const HERO_SLIDES = slides && slides.length ? slides : FALLBACK;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (idx >= HERO_SLIDES.length) setIdx(0);
  }, [HERO_SLIDES.length, idx]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [HERO_SLIDES.length]);

  const slide = HERO_SLIDES[idx] || HERO_SLIDES[0];

  return (
    <section className="relative h-screen w-full overflow-hidden mt-[70px]">
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-[1500ms] ${
            i === idx ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover scale-105 transition-transform duration-[8000ms]"
            style={{ transform: i === idx ? 'scale(1.1)' : 'scale(1.0)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-[1440px] mx-auto w-full px-4 lg:px-12">
          <div className="max-w-2xl text-white">
            <span className="inline-block text-[#a3d859] text-xs font-bold tracking-[4px] mb-4 animate-in fade-in slide-in-from-bottom-3 duration-700">
              {slide.tag}
            </span>
            <h1
              key={slide.id + 'title'}
              className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6 animate-in fade-in slide-in-from-bottom-5 duration-700"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {slide.title}
            </h1>
            <p
              key={slide.id + 'sub'}
              className="text-lg md:text-xl text-white/85 mb-8 max-w-xl leading-relaxed animate-in fade-in slide-in-from-bottom-7 duration-700"
            >
              {slide.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#discover"
                className="inline-flex items-center gap-2 bg-[#8ec045] hover:bg-[#7cad38] text-white px-8 py-3.5 text-[13px] font-bold tracking-wider transition-all duration-300 hover:gap-3"
              >
                DISCOVER MORE
                <ChevronRight className="w-4 h-4" />
              </a>
              <button
                onClick={onPlayFilm}
                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/40 hover:bg-white/20 text-white px-6 py-3.5 text-[13px] font-bold tracking-wider transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-[#E10D0D] flex items-center justify-center">
                  <Play className="w-3 h-3 text-white fill-white ml-0.5" />
                </span>
                WATCH BRAND FILM
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={() => setIdx((idx - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 border border-white/40 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => setIdx((idx + 1) % HERO_SLIDES.length)}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/10 border border-white/40 hover:bg-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? 'w-10 bg-[#8ec045]' : 'w-6 bg-white/40'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 right-8 z-20 hidden md:flex flex-col items-center gap-2 text-white/70">
        <span className="text-[10px] tracking-[3px] rotate-90 origin-center mb-6">SCROLL</span>
        <div className="w-px h-16 bg-white/40" />
      </div>
    </section>
  );
};

export default Hero;
